import React, {useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/src/shape-component"
import AdminButton from "components/admin/button"
import AdminInput from "components/admin/input"
import {AdminSelectContactTypes} from "components/contact-types/select"
import {AdminSelectCountries} from "components/countries/select"
import {AdminSelectCountryStates} from "components/country-states/select"
import {AdminSelectUsers} from "components/users/select"
import AppHistory from "shared/history"
import {Contact} from "models"
import {dig} from "diggerize"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import memo from "set-state-compare/src/memo"
import presence from "shared/presence"
import Routes from "shared/routes"
import {useAdminLayout} from "components/admin/layout"
import useI18n from "i18n-on-steroids/src/use-i18n.mjs"
import useModel from "@kaspernj/api-maker/build/use-model"
import {v4 as uuidv4} from "uuid"
import {View} from "react-native"

export default memo(shapeComponent(class RoutesAdminContactsEdit extends ShapeComponent {
  setup() {
    const {t} = useI18n({namespace: "js.routes.admin.contacts.edit"})
    const {contact, contactId} = useModel(Contact, {
      match: this.p.match,
      newIfNoId: {
        defaults: () => ({id: uuidv4()})
      },
      preload: ["contact_type", "user"],
      select: {
        Contact: ["accountManagerId", "countryId", "countryStateId", "firstName", "id", "lastName", "name", "phoneNumber"],
        ContactType: ["id", "identifier"]
      }
    })

    this.setInstance({contact, contactId, t})
    this.useStates({
      accountManagerId: null,
      countryId: null,
      countryStateId: null,
      contactType: null,
      firstName: "",
      form: null,
      lastName: "",
      name: ""
    })

    useMemo(() => {
      if (contact) {
        this.setState({
          accountManagerId: contact.accountManagerId(),
          countryId: contact.countryId(),
          countryStateId: contact.countryStateId(),
          contactType: contact.contactType(),
          firstName: contact.firstName() || "",
          lastName: contact.lastName() || "",
          name: contact.name() || ""
        })
      }
    }, [contact?.id()])

    useAdminLayout()?.setState({active: "contacts", headerTitle: this.headerTitle()})
  }

  render() {
    const {contact} = this.tt
    const {accountManagerId, contactType, countryId, countryStateId} = this.s
    const accountManagerValues = useMemo(() => (accountManagerId ? [accountManagerId] : null), [accountManagerId])
    const contactTypeIdentifier = contactType?.identifier()
    const countryValues = useMemo(() => (countryId ? [countryId] : null), [countryId])
    const countryStateValues = useMemo(() => (countryStateId ? [countryStateId] : null), [countryStateId])

    return (
      <View dataSet={this.rootViewDataSet ||= {route: "admin/contacts/edit"}}>
        {contact &&
          <Form onSubmit={this.tt.onSubmit} setForm={this.setStates.form}>
            <View style={{marginTop: 10, marginBottom: 20}}>
              <AdminSelectContactTypes
                attribute="contactTypeId"
                id="contact_contact_type_id"
                label={Contact.humanAttributeName("contactType")}
                name="contact[contact_type_id]"
                onChange={this.tt.onContactTypeChange}
                values={contactType ? [contactType.id()] : null}
              />
            </View>
            {contactTypeIdentifier != "person" &&
              <AdminInput
                dataSet={{class: "contact-name-input"}}
                label={Contact.humanAttributeName("name")}
                model={contact}
                name="contact[name]"
                onChangeText={this.setStates.name}
                style={this.contactNameInputStyle ||= {marginBottom: 10}}
                value={this.s.name}
              />
            }
            {contactTypeIdentifier == "person" &&
              <>
                <AdminInput
                  dataSet={{class: "contact-first-name-input"}}
                  label={Contact.humanAttributeName("firstName")}
                  model={contact}
                  name="contact[first_name]"
                  onChangeText={this.setStates.firstName}
                  style={this.contactFirstNameInputStyle ||= {marginBottom: 10}}
                  value={this.s.firstName}
                />
                <AdminInput
                  dataSet={{class: "contact-last-name-input"}}
                  label={Contact.humanAttributeName("lastName")}
                  model={contact}
                  name="contact[last_name]"
                  onChangeText={this.setStates.lastName}
                  style={this.contactLastNameInputStyle ||= {marginBottom: 10}}
                  value={this.s.lastName}
                />
              </>
            }
            {contactTypeIdentifier != "school_group" &&
              <View style={{marginVertical: 10}}>
                <AdminSelectUsers
                  attribute="accountManagerId"
                  id="contact_account_manager_id"
                  label={Contact.humanAttributeName("accountManager")}
                  name="contact[account_manager_id]"
                  onChange={this.tt.onChangeAccountManager}
                  values={accountManagerValues}
                />
              </View>
            }
            <View style={{marginVertical: 10}}>
              <AdminSelectCountries
                attribute="countryId"
                id="contact_country_id"
                label={Contact.humanAttributeName("country")}
                name="contact[country_id]"
                values={countryValues}
              />
            </View>
            <View style={{marginVertical: 10}}>
              <AdminSelectCountryStates
                attribute="countryStateId"
                id="contact_country_state_id"
                label={Contact.humanAttributeName("countryState")}
                name="contact[country_state_id]"
                values={countryStateValues}
              />
            </View>
            <AdminButton primary save submit />
          </Form>
        }
      </View>
    )
  }

  headerTitle() {
    const {contact, t} = this.tt

    if (contact?.isNewRecord()) {
      return t(".add_new_contact")
    } else if (contact?.isPersisted()) {
      return t(".edit_contact", {name: presence(contact.name()) || `${contact.firstName()} ${contact.lastName()}`})
    }
  }

  onChangeAccountManager = ({options}) => this.setState({accountManagerId: dig(options, 0, "value")})

  onContactTypeChange = ({options}) => {
    const contactType = dig(options, 0, "contactType")

    this.setState({contactType})
  }

  onSubmit = async () => {
    const {contact, t} = this.tt
    const formData = this.s.form.asObject()

    if (contact.isNewRecord()) {
      formData.contact.new_id = contact.id()
    }

    try {
      await contact.saveRaw(formData)
      FlashMessage.success(t(".the_contact_was_saved"))
      AppHistory.push(Routes.adminContactPath(contact.id()))
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }
}))
